import { dateRange, type LocalDate } from './dates.js'
import { FREEZE_CAP, FREEZE_MILESTONES, STREAK_MILESTONES } from './economy.js'
import type { WindowState } from './ids.js'

/**
 * Streak and freeze tokens, ARCHITECTURE §4.7 and MASTERPLAN §5.4.
 * The streak counts consecutive local days with at least one verified brush. A partial only
 * day holds the streak. A day with nothing breaks it unless a Dondurucu is available.
 */

export type DayVerdict = 'verified' | 'partial' | 'none'
export type StreakDay = { date: LocalDate; verdict: DayVerdict }

export type StreakResult = {
  current: number
  best: number
  freezeTokens: number
  lastVerifiedDate: LocalDate | null
  /** Days a Dondurucu covered, in order. The app announces these the next morning. */
  freezeUsedDates: LocalDate[]
}

/** A day counts as verified when either window is verified, partial when either is partial. */
export function dayVerdict(day: { morning: WindowState; evening: WindowState }): DayVerdict {
  if (day.morning === 'verified' || day.evening === 'verified') return 'verified'
  if (day.morning === 'partial' || day.evening === 'partial') return 'partial'
  return 'none'
}

export function streakDays(
  calendar: Readonly<Record<string, { morning: WindowState; evening: WindowState }>>,
  from: LocalDate,
  to: LocalDate,
): StreakDay[] {
  return dateRange(from, to).map((date) => {
    const day = calendar[date]
    return { date, verdict: day ? dayVerdict(day) : 'none' }
  })
}

export function computeStreak(days: readonly StreakDay[], today: LocalDate): StreakResult {
  let current = 0
  let best = 0
  let freezeTokens = 0
  let lastVerifiedDate: LocalDate | null = null
  const freezeUsedDates: LocalDate[] = []

  for (const day of days) {
    if (day.verdict === 'verified') {
      current += 1
      if (current > best) best = current
      lastVerifiedDate = day.date
      // A token per milestone reached, never more than the cap in hand.
      if (FREEZE_MILESTONES.includes(current) && freezeTokens < FREEZE_CAP) {
        freezeTokens += 1
      }
      continue
    }
    if (day.verdict === 'partial') continue
    // Today has not happened yet: it counts only once it is verified.
    if (day.date === today) continue
    // A Dondurucu only protects a running streak; there is nothing to protect at zero.
    if (current > 0 && freezeTokens > 0) {
      freezeTokens -= 1
      freezeUsedDates.push(day.date)
      continue
    }
    current = 0
  }

  return { current, best, freezeTokens, lastVerifiedDate, freezeUsedDates }
}

/** Milestone rows the child has earned, for the fold's once each reward. */
export function milestoneRewards(best: number): typeof STREAK_MILESTONES {
  return STREAK_MILESTONES.filter((m) => m.days <= best)
}
